/**
 * The shared route page has no logged-in user, so it has no feature flag
 * identity. The link carries the sender's setting instead. Building and parsing
 * live together so the parameter name exists in one place.
 */

/** Reads as a sentence to anyone who looks at the link they were sent. */
const DISTANCE_PARAM = "distance"
const DISTANCE_BETWEEN_STOPS = "between-stops"

export type SharedRouteSettings = {
  /** Show the leg from the previous stop, not the distance from the vehicle. */
  showLegDistance: boolean
}

/** @param baseUrl - Application origin, with a trailing slash */
export const buildSharedRouteLink = (
  baseUrl: string,
  secret: string | undefined,
  settings: SharedRouteSettings,
): string => {
  const link = `${baseUrl}shared-route/${secret}`

  return settings.showLegDistance
    ? `${link}?${DISTANCE_PARAM}=${DISTANCE_BETWEEN_STOPS}`
    : link
}

/**
 * An absent parameter means the behaviour that shipped before this feature, so
 * links sent earlier keep rendering the way they did when they were sent.
 *
 * @param source - A full URL, or a query string such as `window.location.search`
 */
export const parseSharedRouteSettings = (
  source: string | undefined,
): SharedRouteSettings => {
  if (!source) return { showLegDistance: false }

  const queryString = source.includes("?")
    ? source.slice(source.indexOf("?") + 1)
    : source

  const params = new URLSearchParams(queryString)

  return {
    showLegDistance: params.get(DISTANCE_PARAM) === DISTANCE_BETWEEN_STOPS,
  }
}
